import { sanitizeHtml } from "@/lib/sanitize";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { Instagram, Youtube, Linkedin, Facebook, Mail, Phone, MapPin, Heart } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { ProductFilters, type ProductFilterValues } from "@/components/ProductFilters";
import { Reviews } from "@/components/Reviews";
import { getVendor, listProducts } from "@/lib/catalog.functions";
import { trackVendorVisit } from "@/lib/vendor-visits.functions";
import { formatPhone } from "@/lib/formatPhone";
import { vendorImageAlt } from "@/lib/image-alt";

const searchSchema = z.object({
  q: z.string().optional(),
  min: z.coerce.number().min(0).max(1000000).optional(),
  max: z.coerce.number().min(0).max(1000000).optional(),
  sort: z.enum(["new", "low", "high", "popular"]).optional(),
});

type VendorFaq = { q: string; a: string };

function vendorFaq(name: string, city: string | null, categories: string[]): VendorFaq[] {
  const catText = categories.length
    ? categories.slice(0, 4).join(", ").toLowerCase()
    : "raznovrstne izdelke";
  return [
    {
      q: `Kaj ponuja ${name}?`,
      a: `${name}${city ? ` iz kraja ${city}` : ""} na kupish.si ponuja ${catText}. Vsi izdelki so novi, cene in zaloga pa so razvidne pri posameznem izdelku.`,
    },
    {
      q: `Kako stopim v stik s ponudnikom?`,
      a: `Na profilu trgovine ${name} so navedeni kontaktni podatki, pri vsakem izdelku pa je na voljo obrazec za povpraševanje. Odgovor običajno prejmete v enem delovnem dnevu.`,
    },
    {
      q: `Kako poteka dostava izdelkov?`,
      a: `Izdelke odpošlje ponudnik sam, praviloma v nekaj delovnih dneh po potrditvi naročila. Stroški dostave in morebitni prag za brezplačno pošiljanje so navedeni pri posameznem izdelku.`,
    },
    {
      q: `Kdo je prodajalec in kdo izda račun?`,
      a: `Prodajalec je ${name}. Ponudnik izda račun, odpošlje izdelek ter odgovarja za garancijo, vračila in reklamacije, kupish.si pa upravlja nakupovalno središče in tehnično platformo.`,
    },
  ];
}

export const Route = createFileRoute("/prodajalci/$slug")({
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    const res = await getVendor({ data: { slug: params.slug } });
    const v = res?.vendor as { store_name?: string; description?: string | null; city?: string | null; image_url?: string | null; phone?: string | null; instagram?: string | null; facebook?: string | null; youtube?: string | null; linkedin?: string | null; tiktok?: string | null } | null;
    if (!v) throw notFound();
    return {
      initial: res,
      meta: {
        name: v.store_name ?? params.slug,
        description: v.description ?? null,
        city: v.city ?? null,
        image: v.image_url ?? null,
        phone: v.phone ?? null,
        categories: res?.categories ?? [],
        productCount: res?.productCount ?? 0,
        priceMin: res?.priceMin ?? null,
        priceMax: res?.priceMax ?? null,
        social: [v.instagram, v.facebook, v.youtube, v.linkedin, v.tiktok].filter(Boolean) as string[],
      },
    };
  },
  head: ({ params, loaderData }) => {
    const m = loaderData?.meta;
    const url = `https://kupish.si/prodajalci/${params.slug}`;
    const name = m?.name ?? params.slug;
    const cats = m?.categories ?? [];
    const catNames = cats.slice(0, 3).map((c) => c.name.toLowerCase());
    const longTitle = `${name} — izdelki in ponudba${m?.city ? ` iz kraja ${m.city}` : ""} | kupish.si`;
    const title = longTitle.length <= 70 ? longTitle : `${name} — izdelki in ponudba${m?.city ? ` iz kraja ${m.city}` : ""}`;
    const cleaned = (m?.description ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const generated = `Spoznajte ponudnika ${name}${m?.city ? ` iz kraja ${m.city}` : ""}: ${m?.productCount ? `${m.productCount} izdelkov v ponudbi` : "aktualna ponudba izdelkov"}${catNames.length ? ` (${catNames.join(", ")})` : ""}. Varen nakup v spletnem nakupovalnem središču kupish.si.`;
    const desc = (cleaned.length >= 70 ? cleaned : generated).slice(0, 158);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: m ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "profile" },
        { property: "profile:username", content: params.slug },
        { property: "og:locale", content: "sl_SI" },
        { property: "og:site_name", content: "kupish.si" },
        { name: "twitter:card", content: m?.image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(m?.image ? [
          { property: "og:image", content: m.image.startsWith("http") ? m.image : `https://kupish.si${m.image}` },
          { property: "og:image:secure_url", content: m.image.startsWith("http") ? m.image : `https://kupish.si${m.image}` },
          { property: "og:image:alt", content: `${name} — izdelki in ponudba` },
          { name: "twitter:image", content: m.image.startsWith("http") ? m.image : `https://kupish.si${m.image}` },
          { name: "twitter:image:alt", content: `${name} — izdelki in ponudba` },
        ] : []),
        ...(m?.productCount ? [
          { name: "twitter:label1", content: "Izdelkov" },
          { name: "twitter:data1", content: String(m.productCount) },
        ] : []),
        ...(m?.city ? [
          { name: "twitter:label2", content: "Lokacija" },
          { name: "twitter:data2", content: m.city },
        ] : []),

      ],
      links: [
        { rel: "canonical", href: url },
        ...(m?.image ? [{ rel: "preload", as: "image", href: m.image, fetchpriority: "high" }] : []),
      ],
      scripts: m ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Store",
            name: m.name,
            description: desc,
            url,
            ...(m.image ? { image: m.image } : {}),
            ...(m.phone ? { telephone: m.phone } : {}),
            ...(m.city ? { address: { "@type": "PostalAddress", addressLocality: m.city, addressCountry: "SI" } } : {}),
            ...(m.social.length ? { sameAs: m.social } : {}),
            ...(m.priceMin && m.priceMax ? { priceRange: `${Math.round(m.priceMin)}–${Math.round(m.priceMax)} €` } : {}),
            areaServed: { "@type": "Country", name: "Slovenija" },
            currenciesAccepted: "EUR",
            makesOffer: cats.slice(0, 5).map((c) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Product", name: `${c.name} — ročno delo (${name})` },
              url: `https://kupish.si/kategorije/${c.slug}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: vendorFaq(name, m.city, cats.map((c) => c.name)).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
              { "@type": "ListItem", position: 2, name: "Prodajalci", item: "https://kupish.si/prodajalci" },
              { "@type": "ListItem", position: 3, name, item: url },
            ],
          }),
        },
      ] : [],
    };
  },
  notFoundComponent: () => (
    <section className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Prodajalec ni najden</h1>
      <p className="mt-4 text-muted-foreground">Profil ne obstaja ali trenutno ni objavljen.</p>
      <Link to="/prodajalci" className="mt-6 inline-block text-accent">Poglejte druge ponudnike</Link>
    </section>
  ),
  component: Page,
});

type VendorWithContact = {
  id: string;
  store_name: string;
  city: string | null;
  description: string | null;
  image_url: string | null;
  banner_url: string | null;
  instagram: string | null;
  youtube: string | null;
  linkedin: string | null;
  facebook: string | null;
  tiktok: string | null;
  status: string;
  email: string | null;
  phone: string | null;
};

function ensureUrl(url: string | null, host?: string): string | null {
  if (!url) return null;
  const trimmed = url.trim().replace(/^@/, "");
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (host && !trimmed.includes("/") && !trimmed.includes(".")) {
    const sep = host.endsWith("/") ? "" : "/";
    return `https://${host}${sep}${trimmed}`;
  }
  return `https://${trimmed}`;
}

function Page() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/prodajalci/$slug" });
  const fetchVendor = useServerFn(getVendor);
  const fetchProducts = useServerFn(listProducts);
  const trackVisit = useServerFn(trackVendorVisit);
  const initial = Route.useLoaderData()?.initial ?? undefined;
  const { data, isLoading } = useQuery({
    queryKey: ["vendor", slug],
    queryFn: () => fetchVendor({ data: { slug } }),
    ...(initial ? { initialData: initial } : {}),
  });
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ["vendor-products", slug, search],
    queryFn: () => fetchProducts({ data: {
      vendorSlug: slug,
      q: search.q,
      priceMin: search.min,
      priceMax: search.max,
      sort: search.sort ?? "popular",
      limit: 120,
    } }),
  });

  const vendorId = (data?.vendor as { id?: string } | undefined)?.id;
  useEffect(() => {
    if (!vendorId) return;
    trackVisit({ data: { vendorId, kind: "shop", path: `/prodajalci/${slug}` } }).catch(() => {});
  }, [vendorId, slug, trackVisit]);

  const updateFilters = (patch: Partial<ProductFilterValues>) => {
    navigate({ search: (prev: Record<string, unknown>) => {
      const next: Record<string, unknown> = { ...prev, ...patch };
      Object.keys(next).forEach((k) => {
        if (next[k] == null || next[k] === "") delete next[k];
      });
      return next;
    } });
  };

  if (isLoading) return <div className="mx-auto max-w-5xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  if (!data?.vendor) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">Prodajalec ne obstaja</h1>
        <Link to="/" className="mt-6 inline-block underline underline-offset-4">Nazaj na domačo stran</Link>
      </div>
    );
  }

  const vendor = data.vendor as unknown as VendorWithContact;
  const vendorCategories = (data.categories ?? []) as Array<{ name: string; slug: string; count: number }>;
  const productCount = data.productCount ?? 0;
  const faq = vendorFaq(vendor.store_name, vendor.city, vendorCategories.map((c) => c.name));
  const instagram = ensureUrl(vendor.instagram, "instagram.com");
  const youtube = ensureUrl(vendor.youtube, "youtube.com");
  const linkedin = ensureUrl(vendor.linkedin, "linkedin.com/in");
  const facebook = ensureUrl(vendor.facebook, "facebook.com");
  const tiktok = ensureUrl(vendor.tiktok, "tiktok.com/@");
  const hasSocial = instagram || youtube || linkedin || facebook || tiktok;

  return (
    <section>
      <div className="mx-auto max-w-7xl px-6 pt-10 md:pt-14">
        {/* Banner + centered profile */}
        <div className="relative">
          {vendor.banner_url && (
            <div className="relative aspect-[4/1] w-full overflow-hidden border border-border bg-muted">
              <img
                src={vendor.banner_url}
                alt={vendorImageAlt(vendor.store_name, "banner", vendor.city)}
                className="h-full w-full object-cover"
                width={1600}
                height={400}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          )}
          {vendor.image_url && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 z-20">
              <img
                src={vendor.image_url}
                alt={vendorImageAlt(vendor.store_name, "logo", vendor.city)}
                className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] rounded-full object-cover border border-border bg-background shadow-md"
                width={120}
                height={120}
                loading="eager"
                decoding="async"
              />
            </div>
          )}
        </div>

        {/* Header card */}
        <div className={`bg-background border border-border p-6 md:p-10 pt-16 md:pt-20 shadow-sm relative z-10 ${vendor.banner_url ? "border-t-0" : ""}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="eyebrow">Prodajalec</div>
              <h1 className="font-serif text-4xl md:text-5xl mt-2 text-brand">{vendor.store_name}</h1>
            </div>


            <div className="flex items-center gap-2 shrink-0 pt-1">
          {hasSocial && (
                <div className="flex items-center gap-2">
                  {instagram && (
                    <a
                      href={instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <Instagram className="h-4 w-4" />
                    </a>
                  )}
                  {youtube && (
                    <a
                      href={youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <Youtube className="h-4 w-4" />
                    </a>
                  )}
                  {linkedin && (
                    <a
                      href={linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <Linkedin className="h-4 w-4" />
                    </a>
                  )}
                  {facebook && (
                    <a
                      href={facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>
                  )}
                  {tiktok && (
                    <a
                      href={tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="TikTok"
                      className="w-9 h-9 flex items-center justify-center border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                        <path d="M9 12a4 4 0 1 0 4 4V4c0 1.5.8 3 2.5 3.5"/>
                        <path d="M19 8v8"/>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            {vendor.city && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {vendor.city}
              </span>
            )}
            {vendor.status === "active" && (
              <span className="inline-flex items-center gap-1.5 text-accent">● aktiven</span>
            )}
          </div>


          {(vendor.email || vendor.phone) && (
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              {vendor.email && (
                <a
                  href={`mailto:${vendor.email}`}
                  onClick={() => { trackVisit({ data: { vendorId: vendor.id, kind: "email", path: `/prodajalci/${vendor.id}` } }).catch(() => {}); }}
                  className="inline-flex items-center gap-2 hover:text-accent transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  {vendor.email}
                </a>
              )}
              {vendor.phone && (
                <a
                  href={`tel:${vendor.phone.replace(/\s+/g, "")}`}
                  onClick={() => { trackVisit({ data: { vendorId: vendor.id, kind: "phone", path: `/prodajalci/${vendor.id}` } }).catch(() => {}); }}
                  className="inline-flex items-center gap-2 hover:text-accent transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {formatPhone(vendor.phone)}
                </a>
              )}
            </div>
          )}

          {/* Dejstveni povzetek — strnjeni podatki, ki jih AI-iskalniki najlažje citirajo. */}
          <dl className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2 max-w-3xl border-y border-border py-5 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0">Prodajalec:</dt>
              <dd className="font-medium">{vendor.store_name}</dd>
            </div>
            {vendor.city && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground shrink-0">Lokacija:</dt>
                <dd className="font-medium">{vendor.city}, Slovenija</dd>
              </div>
            )}
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0">Izdelki v trgovini:</dt>
              <dd className="font-medium">{productCount}</dd>
            </div>
            {vendorCategories.length > 0 && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground shrink-0">Ponudba:</dt>
                <dd className="font-medium">
                  {vendorCategories.slice(0, 3).map((c) => c.name.toLowerCase()).join(", ")}
                </dd>
              </div>
            )}
            <div className="flex gap-2">
              <dt className="text-muted-foreground shrink-0">Način izdelave:</dt>
              <dd className="font-medium">novi izdelki</dd>
            </div>
          </dl>

          {vendor.description ? (
            <div
              className="mt-8 max-w-3xl text-muted-foreground leading-relaxed prose prose-sm"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(vendor.description) }}
            />
          ) : (
            <p className="mt-8 max-w-3xl text-muted-foreground leading-relaxed">
              {vendor.store_name}{vendor.city ? ` iz kraja ${vendor.city}` : ""} na kupish.si ponuja
              {vendorCategories.length ? ` ${vendorCategories.slice(0, 3).map((c) => c.name.toLowerCase()).join(", ")}` : " raznovrstne izdelke"}.
              {productCount ? ` V trgovini je trenutno ${productCount} objavljenih izdelkov,` : ""} vsak nastane ročno v manjši seriji,
              zato se kosi med seboj rahlo razlikujejo — prav v tem je njihova vrednost.
            </p>
          )}

          {vendorCategories.length > 0 && (
            <div className="mt-8">
              <h2 className="font-serif text-xl">Kategorije izdelkov ponudnika</h2>
              <ul className="mt-3 flex flex-wrap gap-2">
                {vendorCategories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to="/kategorije/$slug"
                      params={{ slug: c.slug }}
                      className="inline-flex items-center gap-2 border border-border px-3 py-1.5 text-sm hover:border-accent hover:text-accent transition-colors"
                    >
                      {c.name}
                      <span className="text-muted-foreground">{c.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>


        {/* Products */}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 pb-6">
          <div className="flex items-center gap-4 md:gap-6">
            <h2 className="font-serif text-3xl">Izdelki ({productsData?.total ?? 0})</h2>
            <div className="hidden md:flex items-center gap-3 text-muted-foreground">
              <span className="h-px w-10 md:w-16 bg-border" />
              <Heart className="h-4 w-4 text-accent fill-current" />
              <span className="h-px w-10 md:w-16 bg-border" />
            </div>
          </div>
          <ProductFilters
            values={{ q: search.q, min: search.min, max: search.max, sort: search.sort }}
            onChange={updateFilters}
            onReset={() => navigate({ search: {} })}
            showSearch={false}
            className="mt-0 border-0 pb-0"
          />
        </div>
          {productsLoading ? (
            <p className="mt-10 text-muted-foreground">Nalagam…</p>
          ) : (productsData?.products ?? []).length === 0 ? (
            <p className="mt-10 text-muted-foreground">Ni zadetkov za izbrane filtre.</p>
          ) : (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
              {(productsData?.products ?? []).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}

        <section className="pt-16">
          <h2 className="font-serif text-3xl">Pogosta vprašanja o ponudniku</h2>
          <div className="mt-6 max-w-3xl divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-4">
                <summary className="cursor-pointer list-none font-medium marker:hidden">{f.q}</summary>
                <p className="mt-2 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="py-16">
          <Reviews vendorId={vendor.id} />
        </div>
      </div>
    </section>
  );
}
